import log from "logbeacon";
import { useInfiniteQuery } from "@tanstack/react-query";
import { type Spec } from "@json-render/react";
import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { wrapperResult } from "@api/osp";
import { NOTIFY_MESSAGE_MOCK_ROWS } from "./__mocks__/notifyMessageMockData";
import { useOspApiClientProvider } from "@hooks";
import { rootStore } from "@store";
import { QUERY_KEYS } from "@config/constant";
import {
  getNotifyMessageReadMap,
  isNotifyMessageRead,
} from "@/src/components/Notification/localNotificationState";
import type {
  Message,
  MessagePagination,
  NotifyClient,
} from "@/src/features/api/NotifyApi";
import {
  parseNotifyMessageContent,
  resolveNotificationBody,
} from "@/src/screens/notifications/getNotifyMessageSpec";
import { useNotificationSyncVersion } from "@/src/features/notification/sync/useNotificationSyncVersion";

const ENABLE_NOTIFY_MESSAGE_MOCK = false;
const PAGE_SIZE = 20;
const DEFAULT_NOTIFY_BUSINESS_TYPES = [
  "PROACTIVE_STEWARD",
  "CUSTOM_TASK",
] as const;

export type NotifyListMessageQueryParams = Omit<
  Parameters<NotifyClient<any>["message"]["listMessage"]>[0],
  "next_token"
>;

export interface UseNotifyMessageInfiniteOptions {
  /**
   * List filters and page size; `next_token` is managed by the hook.
   * Memoize when stable to avoid redundant refetches.
   */
  queryParams?: Partial<NotifyListMessageQueryParams>;
  /** Combined with login state: requests only run when logged in. */
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export interface UseNotifyMessageInfiniteResult {
  /** All loaded pages merged and filtered to valid structured notifications. */
  rows: NotifyMessageRow[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: ReturnType<typeof useInfiniteQuery>["fetchNextPage"];
  refetch: ReturnType<typeof useInfiniteQuery>["refetch"];
  error: unknown;
}

export interface NotifyMessageRow extends Message {
  spec: Spec;
  contentTitle: string | null;
  collapsed: boolean;
  isRead: boolean;
}

/**
 * Infinite list for GET /message (notify) using cursor `next_token`.
 * Pass all required query fields via `queryParams` (except `next_token`).
 *
 * Internally each page is `MessagePagination` (for cursor); the hook exposes only
 * merged `rows` (`Message[]`). `wrapperResult` returns inner `data` payload.
 */
export const useNotifyMessageInfinite = ({
  queryParams,
  enabled = true,
  staleTime = 30 * 1000,
  gcTime = 30 * 1000,
}: UseNotifyMessageInfiniteOptions): UseNotifyMessageInfiniteResult => {
  const { notifyApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const notificationSyncVersion = useNotificationSyncVersion();
  const mockRows = useMemo(() => NOTIFY_MESSAGE_MOCK_ROWS, []);
  const resolvedQueryParams = useMemo<NotifyListMessageQueryParams>(
    () => ({
      ...queryParams,
      business_types: queryParams?.business_types ?? [
        ...DEFAULT_NOTIFY_BUSINESS_TYPES,
      ],
      limit: queryParams?.limit ?? PAGE_SIZE,
      owner_id: ownerInfo?.id ?? queryParams?.owner_id,
    }),
    [ownerInfo?.id, queryParams],
  );

  const result = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.NOTIFY_MESSAGE_INFINITE,
      resolvedQueryParams,
      isLoggedIn,
    ],
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<MessagePagination> => {
      if (!notifyApi?.message?.listMessage) {
        throw new Error("Notify message API is not available");
      }
      log.debug("[useNotifyMessageInfinite] query", {
        ...resolvedQueryParams,
        next_token: pageParam,
      });
      const data = await wrapperResult(
        notifyApi.message.listMessage({
          ...resolvedQueryParams,
          ...(pageParam !== undefined && pageParam !== ""
            ? { next_token: pageParam }
            : {}),
        }),
      );
      return data as MessagePagination;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const token = lastPage?.next_token;
      if (typeof token === "string" && token.length > 0) {
        return token;
      }
      return undefined;
    },
    enabled:
      !ENABLE_NOTIFY_MESSAGE_MOCK && !!notifyApi && isLoggedIn && enabled,
    staleTime,
    gcTime,
  });

  const sourceRows = useMemo(
    () =>
      ENABLE_NOTIFY_MESSAGE_MOCK
        ? mockRows
        : (result.data?.pages.flatMap((page) => page.rows ?? []) ?? []),
    [mockRows, result.data],
  );

  const rows = useMemo<NotifyMessageRow[]>(() => {
    const ownerId = ownerInfo?.id;
    const notifyMessageReadMap = getNotifyMessageReadMap(ownerId);
    const now = Date.now();

    return sourceRows
      .map((message) => {
        const parsedContent = parseNotifyMessageContent(message.content);
        const contentTitle = parsedContent?.title ?? null;
        const messageId =
          typeof message.id === "string" && message.id.length > 0
            ? message.id
            : undefined;
        const createdAt =
          typeof message.created === "number" ? message.created : undefined;

        const { spec } = resolveNotificationBody(parsedContent?.body, {
          recordId: messageId,
          created: createdAt,
          title: contentTitle ?? undefined,
        });

        return {
          ...message,
          spec,
          contentTitle,
          collapsed: true,
          isRead:
            typeof createdAt === "number" &&
            now - createdAt > 30 * 24 * 60 * 60 * 1000
              ? true
              : messageId
                ? notifyMessageReadMap.has(messageId)
                : isNotifyMessageRead(ownerId, messageId, createdAt, now),
        };
      })
      .filter((item): item is NotifyMessageRow => item.spec !== null);
  }, [ownerInfo?.id, notificationSyncVersion, sourceRows]);

  const total = rows.length;

  return {
    rows,
    total,
    isLoading: ENABLE_NOTIFY_MESSAGE_MOCK ? false : result.isLoading,
    isFetching: ENABLE_NOTIFY_MESSAGE_MOCK ? false : result.isFetching,
    isFetchingNextPage: ENABLE_NOTIFY_MESSAGE_MOCK
      ? false
      : result.isFetchingNextPage,
    hasNextPage: ENABLE_NOTIFY_MESSAGE_MOCK ? false : result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    refetch: result.refetch,
    error: ENABLE_NOTIFY_MESSAGE_MOCK ? null : result.error,
  };
};
